import i18n, { I18N_NS } from "Config/i18n";
import { Modal } from "antd";
import { ModalProps } from "antd/lib/modal";
import React, { PropsWithChildren } from "react";
import { WithModal } from "./withModal";
import hashSum from "hash-sum";
import { modalPositionsStore } from "./ModalPositionsStore";
import { stringify } from "./GenericModal";
import { modal } from "./AntdModal";
import { MODAL_DEFAULT_Z_INDEX } from "./DnDModalInContext";

// tslint:disable-next-line: max-line-length
export type ShowModalFunction = <P>(c: React.ComponentType<P>, config?: ModalConfig<P>) => void;

/**
 * Extend component props with this interface
 */
export interface ModalComponentProps {
  /* Hides the current modal window */
  hideModal?: () => void;
  /* when set to "true" - will show prompt before closing modal */
  setConfirmOnCancel?: (b: boolean) => void;
  /* registers a save callback invoked when user confirms save on the "unsaved changes" dialog */
  setOnSave?: (fn: () => void) => void;
}

export interface ModalConfirmConfig {
  title?: string;
  content?: string;
  okText?: string;
  cancelText?: string;
  /** When true, the modal closes on the Cancel button instead of the OK button */
  closeOnCancel?: boolean;
}

export interface ModalConfig<P = {}> {
  modalProps?: ModalProps;
  componentProps?: P;
  confirmOnCancel?: boolean;
  /* to disable drag and drop functionality */
  enableDnD?: boolean;
  confirmConfig?: ModalConfirmConfig;
}

export const ModalContext = React.createContext<ModalProviderValue>({
  modalItems: [],
  setConfirmOnCancel: () => (_b: boolean) => void 0,
  setOnSave: () => (_fn: () => void) => void 0,
  showModal: () => (_c, _config) => void 0,
  hideModal: () => () => void 0,
  updateModalPosition: () => () => void 0,
  hideAllModals: () => () => void 0,
});

export interface ModalItemProps {
  uid: string;
  visible: boolean;
  relativePosition?: { top: number; left: number };
  component: React.ComponentType<ModalComponentProps>;
  modalProps: ModalProps;
  componentProps: Object; // Props passed to components (e.g. initial form data etc).
  confirmOnCancel: boolean; // if true - ask before closing the modal
  enableDnD: boolean; // to disable drag and drop functionality
  confirmConfig?: ModalConfirmConfig;
}

interface ModalProviderValue {
  modalItems: ModalItemProps[];
  // confirmOnCancel trigger function. NOTE: Make sure to wait for state change before using "hideModal()"!!
  setConfirmOnCancel: (uid: string) => (b: boolean) => Promise<void>;
  setOnSave: (uid: string) => (fn: () => void) => void;
  showModal: ShowModalFunction;
  hideModal: (uid: string) => void;
  hideAllModals: () => void;
  updateModalPosition: (uid: string) => (position: ModalItemProps["relativePosition"]) => void;
}
interface ModalProviderState {
  modalItems: ModalItemProps[];
}

export class ModalProvider extends React.Component<PropsWithChildren<{}>, ModalProviderState> {
  private onSaveCallbacks: Record<string, () => void> = {};

  constructor(props) {
    super(props);
    this.state = {
      modalItems: [],
    };
  }

  // NOTE: modalPropsHash might be not unique as it's possible that identical modalProps exist
  // initially it was not expected, but I think that if modals look the same - they can share the same position
  makeModalUid = (modalProps: ModalItemProps["modalProps"]) => {
    const modalPropsHash = hashSum(stringify(modalProps));
    return `modal-${modalPropsHash}`;
  };

  showModal: ShowModalFunction = (component, config) => {
    const modalUid = this.makeModalUid(config.modalProps);

    const modalItems = this.state.modalItems.slice();
    const modalToChangeIdx = modalItems.findIndex((item) => item.uid === modalUid);

    if (!modalItems[modalToChangeIdx]) {
      modalItems.push({
        uid: modalUid,
        visible: true,
        relativePosition: modalPositionsStore.getPosition(modalUid),
        component,
        modalProps: config.modalProps,
        componentProps: config.componentProps,
        confirmOnCancel: !!config.confirmOnCancel,
        enableDnD: !!config.enableDnD,
        confirmConfig: config.confirmConfig,
      });
    } else {
      modalItems[modalToChangeIdx] = {
        uid: modalUid,
        visible: true,
        component,
        relativePosition: modalItems[modalToChangeIdx].relativePosition,
        modalProps: config.modalProps,
        componentProps: config.componentProps,
        confirmOnCancel: !!config.confirmOnCancel,
        enableDnD: !!config.enableDnD,
        confirmConfig: config.confirmConfig,
      };
    }
    this.setState({ modalItems });
  };

  setConfirmOnCancel = (uid: string) => async (confirm?: boolean) => {
    return new Promise<void>((resolve) => {
      const modalItems = this.state.modalItems.slice();
      const modalToChangeIdx = modalItems.findIndex((item) => item.uid === uid);
      modalItems[modalToChangeIdx].confirmOnCancel = !!confirm;
      this.setState({ modalItems }, () => resolve());
    });
  };

  setOnSave = (uid: string) => (fn: () => void) => {
    this.onSaveCallbacks[uid] = fn;
  };

  updateModalPosition = (uid: string) => (position: ModalItemProps["relativePosition"]) => {
    const modalItems = this.state.modalItems.slice();
    const modalToChangeIdx = modalItems.findIndex((item) => item.uid === uid);
    if (!modalItems[modalToChangeIdx]) {
      return;
    }
    modalItems[modalToChangeIdx].relativePosition = { ...position };
    modalPositionsStore.setPosition(uid, position);
    this.setState({ modalItems });
  };

  // TODO: don't remove from modalItems, instead remove excess props and preserve position
  hideModal = (uid: string) => {
    const modalItems = this.state.modalItems.slice();
    const modalToChangeIdx = modalItems.findIndex((item) => item.uid === uid);
    if (!modalItems[modalToChangeIdx]) {
      return;
    }
    const reset = () => {
      modalItems[modalToChangeIdx].visible = false;
      modalItems[modalToChangeIdx].component = null;
      this.setState({ modalItems: modalItems });
    };
    if (modalItems[modalToChangeIdx]?.confirmOnCancel) {
      const cc = modalItems[modalToChangeIdx]?.confirmConfig;
      const onSave = this.onSaveCallbacks[uid];
      // make sure to change this together with Modal zIndex
      const parentZIndex = (modalItems[modalToChangeIdx].modalProps?.zIndex as number) || MODAL_DEFAULT_Z_INDEX;
      modal.confirm({
        title: cc?.title || i18n.t("You are going to loose all progress", { ns: I18N_NS.GENERAL }),
        content: cc?.content || i18n.t("Are you sure you want to exit?", { ns: I18N_NS.GENERAL }),
        okText: cc?.okText || i18n.t("Yes", { ns: I18N_NS.GENERAL }),
        okType: "primary",
        cancelText: cc?.cancelText || i18n.t("No", { ns: I18N_NS.GENERAL }),
        zIndex: parentZIndex + 3,
        ...(cc?.closeOnCancel ? { onOk: onSave || undefined, onCancel: reset } : { onOk: reset }),
      });
    } else {
      reset();
    }
  };

  hideAllModals = () => {
    const newModalItems = this.state.modalItems.map((modalItem) => ({
      ...modalItem,
      visible: false,
      component: null,
    }));
    this.setState({ modalItems: newModalItems });
    Modal.destroyAll();
  };

  get providerValue(): ModalProviderValue {
    return {
      showModal: this.showModal,
      hideModal: this.hideModal,
      setConfirmOnCancel: this.setConfirmOnCancel,
      setOnSave: this.setOnSave,
      updateModalPosition: this.updateModalPosition,
      hideAllModals: this.hideAllModals,
      modalItems: this.state.modalItems,
    };
  }

  render() {
    return <ModalContext.Provider value={this.providerValue}>{this.props.children}</ModalContext.Provider>;
  }
}

export const ModalContextConsumer = ModalContext.Consumer;

interface ModalConsumerProps extends WithModal {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (params: { showModal: ShowModalFunction }) => any;
}

export const ModalConsumer = withModal(({ children, showModal }: ModalConsumerProps) => {
  return children({ showModal });
});

interface WithModalComponentState {
  hideModalWithUid: () => void;
  showModalWithUid: ShowModalFunction;
}

export function withModal<T>(WrappedComponent: React.ComponentType<T & WithModal>) {
  class WithModalComponent extends React.Component<T & { forwardRef?: React.RefObject<T> }, WithModalComponentState> {
    static contextType = ModalContext;
    context!: React.ContextType<typeof ModalContext>;
    constructor(props: T) {
      super(props);
      this.state = {
        showModalWithUid: null,
        hideModalWithUid: null,
      };
    }

    render() {
      return (
        <WrappedComponent
          hideAllModals={this.context.hideAllModals}
          showModal={this.context.showModal}
          ref={this.props.forwardRef}
          {...this.props}
        />
      );
    }
  }

  return WithModalComponent;
}
